import React, { useState, useEffect, useRef, useMemo } from 'react';
import { TemplateData } from '../../types/design';
import { LayerRenderer } from './LayerRenderer';
import { useGetCustomApisQuery } from '../../features/integrations/customApiApi';
import { resolveTemplateBindings, resolveJsonPath } from '../../pages/template-editor/utils/bindingResolver';
import { calculateMinTemplateDuration } from '../../utils/animationUtils';

interface TemplatePreviewProps {
    data: TemplateData;
    autoPlay?: boolean;
    loop?: boolean;
    onTimeUpdate?: (time: number) => void;
    width?: number | string;
    height?: number | string;
}

// Helper to extract array binding information
function extractArrayBindingInfo(apiKey: string): { prefix: string; index: number } | null {
    if (!apiKey) return null;

    // Pattern 1: prefix[0] or [0] (e.g. items[0].title or [0].title)
    const bracketMatch = apiKey.match(/^(.*?)\[(\d+)\]/);
    if (bracketMatch) {
        return { prefix: bracketMatch[1], index: parseInt(bracketMatch[2], 10) };
    }

    // Pattern 2: prefix.0.field (e.g. items.0.title)
    const dotMiddleMatch = apiKey.match(/^(.*?)\.(\d+)\./);
    if (dotMiddleMatch) {
        return { prefix: dotMiddleMatch[1], index: parseInt(dotMiddleMatch[2], 10) };
    }

    // Pattern 3: 0.field (root array, e.g. 0.EventName, 1.SpeakerName)
    const rootDotMatch = apiKey.match(/^(\d+)\./);
    if (rootDotMatch) {
        return { prefix: '', index: parseInt(rootDotMatch[1], 10) };
    }

    // Pattern 4: prefix.0 (ends with index, e.g. items.0)
    const dotEndMatch = apiKey.match(/^(.*?)\.(\d+)$/);
    if (dotEndMatch) {
        return { prefix: dotEndMatch[1], index: parseInt(dotEndMatch[2], 10) };
    }

    // Pattern 5: exactly "0" or number
    if (/^\d+$/.test(apiKey)) {
        return { prefix: '', index: parseInt(apiKey, 10) };
    }

    return null;
}

export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
    data,
    autoPlay = true,
    loop = true,
    onTimeUpdate,
    width = '100%',
    height = '100%',
}) => {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const requestRef = useRef<number>();
    const previousTimeRef = useRef<number>();
    const containerRef = useRef<HTMLDivElement>(null);

    const { data: customApiRes } = useGetCustomApisQuery(undefined, {
        refetchOnMountOrArgChange: true,
        pollingInterval: 60000,
    });
    const customApis = customApiRes?.data || [];

    const pagesListOriginal = useMemo(() => (data as any).pages || [], [data]);

    // Support multi-page templates and runtime dynamic pages from API arrays
    const dynamicPages = useMemo(() => {
        const pagesList = pagesListOriginal.length > 0 ? [...pagesListOriginal] : [data];
        if (pagesList.length === 0) return [];

        const basePage = pagesList[0];

        // Find if there is any binding pointing to an array index
        let maxArrayLength = 1;
        let arrayKeyPrefix = '';
        let boundApiId = '';

        const checkBindingForArray = (binding: any) => {
            if (binding?.apiKey && binding?.customApiId) {
                const info = extractArrayBindingInfo(binding.apiKey);
                if (info) {
                    const prefix = info.prefix;
                    const api = customApis.find((a) => String(a._id) === String(binding.customApiId));
                    if (api && api.customapidata) {
                        const arrayVal = prefix === '' ? api.customapidata : resolveJsonPath(api.customapidata, prefix);
                        if (Array.isArray(arrayVal) && arrayVal.length > maxArrayLength) {
                            maxArrayLength = arrayVal.length;
                            arrayKeyPrefix = prefix;
                            boundApiId = String(binding.customApiId);
                        }
                    }
                }
            }
        };

        // Scan the first page bindings
        if (basePage.textStyles) {
            Object.values(basePage.textStyles).forEach((style: any) => checkBindingForArray(style?.binding));
        }
        if (basePage.imageSettings) {
            Object.values(basePage.imageSettings).forEach((setting: any) => checkBindingForArray(setting?.binding));
        }
        if (basePage.scrollerSettings) {
            Object.values(basePage.scrollerSettings).forEach((setting: any) => checkBindingForArray(setting?.binding));
        }

        const autoPaginate = data.templateSettings?.autoPaginate || false;

        if (maxArrayLength <= 1) {
            return pagesList;
        }

        // Helper to clone basePage with specific array index resolved
        const getClonedPageWithIndex = (idx: number) => {
            const cloned = JSON.parse(JSON.stringify(basePage));
            cloned.name = `Page ${idx + 1}`;

            const escapedPrefix = arrayKeyPrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const bracketRegex = arrayKeyPrefix ? new RegExp(`^${escapedPrefix}\\[\\d+\\]`) : /^\[\d+\]/;
            const dotRegex = arrayKeyPrefix ? new RegExp(`^${escapedPrefix}\\.\\d+\\.`) : /^\d+\./;
            const endRegex = arrayKeyPrefix ? new RegExp(`^${escapedPrefix}\\.\\d+$`) : /^\d+$/;

            const rewriteBinding = (binding: any) => {
                if (binding?.apiKey && String(binding.customApiId) === boundApiId) {
                    if (arrayKeyPrefix) {
                        binding.apiKey = binding.apiKey
                            .replace(bracketRegex, `${arrayKeyPrefix}[${idx}]`)
                            .replace(dotRegex, `${arrayKeyPrefix}.${idx}.`)
                            .replace(endRegex, `${arrayKeyPrefix}.${idx}`);
                    } else {
                        binding.apiKey = binding.apiKey
                            .replace(/^\[\d+\]/, `[${idx}]`)
                            .replace(/^\d+\./, `${idx}.`)
                            .replace(/^\d+$/, `${idx}`);
                    }
                }
            };

            if (cloned.textStyles) {
                Object.values(cloned.textStyles).forEach((style: any) => rewriteBinding(style?.binding));
            }
            if (cloned.imageSettings) {
                Object.values(cloned.imageSettings).forEach((setting: any) => rewriteBinding(setting?.binding));
            }
            if (cloned.scrollerSettings) {
                Object.values(cloned.scrollerSettings).forEach((setting: any) => rewriteBinding(setting?.binding));
            }

            return cloned;
        };

        if (!autoPaginate) {
            // Case A: Single item index (1-based user input, maps to index - 1)
            const inputIndex = (data.templateSettings as any)?.singleItemIndex === 'array.length' ? maxArrayLength : (Number((data.templateSettings as any)?.singleItemIndex) || 1);
            const targetIndex = Math.min(Math.max(0, inputIndex - 1), maxArrayLength - 1);
            return [getClonedPageWithIndex(targetIndex)];
        } else {
            // Case B: Multipage with start and end index (1-based user input)
            const inputStart = data.templateSettings?.startIndex || 1;
            const inputEnd = (data.templateSettings as any)?.endIndex === 'array.length' ? maxArrayLength : (Number((data.templateSettings as any)?.endIndex) || maxArrayLength);

            const startIdx = Math.min(Math.max(0, inputStart - 1), maxArrayLength - 1);
            const endIdx = Math.min(Math.max(startIdx, inputEnd - 1), maxArrayLength - 1);

            const generatedPages: any[] = [];
            for (let i = startIdx; i <= endIdx; i++) {
                generatedPages.push(getClonedPageWithIndex(i));
            }
            return generatedPages;
        }
    }, [pagesListOriginal, data, customApis]);

    const pages = dynamicPages;
    const hasMultiplePages = pages.length > 1;
    const minAnimDuration = calculateMinTemplateDuration(data);
    const userInterval = data.templateSettings?.pageInterval ? data.templateSettings.pageInterval * 1000 : null;
    const pageDuration = Math.max(
        minAnimDuration,
        userInterval || (data.templateSettings?.duration || 20000)
    );
    const totalDuration = hasMultiplePages ? pageDuration * pages.length : pageDuration;

    // Playback Loop
    const animate = (time: number) => {
        if (previousTimeRef.current !== undefined && isPlaying) {
            const deltaTime = time - previousTimeRef.current;
            setCurrentTime((prevTime) => {
                let newTime = prevTime + deltaTime;

                if (newTime >= totalDuration) {
                    if (loop) {
                        newTime = 0;
                        setCurrentPageIndex(0);
                    } else {
                        newTime = totalDuration;
                        setIsPlaying(false);
                    }
                } else if (hasMultiplePages) {
                    const newPageIndex = Math.floor(newTime / pageDuration);
                    if (newPageIndex !== currentPageIndex) {
                        setCurrentPageIndex(newPageIndex);
                    }
                }

                onTimeUpdate?.(newTime);
                return newTime;
            });
        }
        previousTimeRef.current = time;
        requestRef.current = requestAnimationFrame(animate);
    };

    useEffect(() => {
        if (isPlaying) {
            requestRef.current = requestAnimationFrame(animate);
        } else {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
            previousTimeRef.current = undefined;
        }
        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
    }, [isPlaying, loop, totalDuration, currentPageIndex]);

    // Handle Scaling
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                setContainerSize({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height
                });
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const currentData = useMemo(() => {
        if (hasMultiplePages && pages[currentPageIndex]) {
            return pages[currentPageIndex];
        }
        return data;
    }, [data, currentPageIndex, hasMultiplePages, pages]);

    // Use current page dimensions for accurate scaling
    const currentPageWidth = currentData.width || currentData.canvasDimensions?.width || width;
    const currentPageHeight = currentData.height || currentData.canvasDimensions?.height || height;

    // CALCULATE PERFECT SCALE BASED ON ACTUAL PROJECT DIMENSIONS
    const calculatedScale = useMemo(() => {
        if (!containerSize.width || !containerSize.height) return 0.1;
        const scaleX = containerSize.width / currentPageWidth;
        const scaleY = containerSize.height / currentPageHeight;

        const finalScale = Math.min(scaleX, scaleY) * 0.98;
        return finalScale;
    }, [containerSize, currentPageWidth, currentPageHeight]);
    
    const layers = useMemo(() => currentData.dynamicLayers || currentData.layers || currentData.elements || [], [currentData]);

    const resolvedBindings = useMemo(() => {
        return resolveTemplateBindings(
            currentData.textContents,
            currentData.textStyles,
            currentData.imageSettings,
            currentData.scrollerSettings,
            customApis
        );
    }, [currentData, customApis]);

    // Adjust current time for the specific page
    const pageCurrentTime = hasMultiplePages ? currentTime % pageDuration : currentTime;

    const goToPage = (index: number) => {
        setCurrentPageIndex(index);
        setCurrentTime(index * pageDuration);
        previousTimeRef.current = performance.now();
    };

    return (
        <div
            ref={containerRef}
            className="group/preview p-8 md:p-12"
            style={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                maxHeight: '100%',
                backgroundColor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
                boxSizing: 'border-box'
            }}
        >
            <div
                style={{
                    width: currentPageWidth * calculatedScale,
                    height: currentPageHeight * calculatedScale,
                    position: 'relative',
                    flexShrink: 0,
                    backgroundColor: currentData.backgroundSettings?.['root']?.type === 'solid' ? currentData.backgroundSettings['root'].solidColor : '#ffffff',
                    backgroundImage: currentData.backgroundSettings?.['root']?.type === 'gradient' 
                        ? `linear-gradient(${currentData.backgroundSettings['root'].gradientAngle}deg, ${currentData.backgroundSettings['root'].gradientStart}, ${currentData.backgroundSettings['root'].gradientEnd})`
                        : (currentData.backgroundSettings?.['root']?.type === 'image' ? `url(${currentData.backgroundSettings['root'].imageUrl})` : undefined),
                    backgroundSize: currentData.backgroundSettings?.['root']?.type === 'image' ? (currentData.backgroundSettings['root'].imageFit || 'cover') : undefined,
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    overflow: 'hidden',
                    opacity: containerSize.width > 0 ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out'
                }}
            >
                <div
                    style={{
                        width: currentPageWidth,
                        height: currentPageHeight,
                        transform: `scale(${calculatedScale}) translate(${ (currentData as any).flipHorizontal ? currentPageWidth : 0 }px, ${ (currentData as any).flipVertical ? currentPageHeight : 0 }px) scaleX(${ (currentData as any).flipHorizontal ? -1 : 1 }) scaleY(${ (currentData as any).flipVertical ? -1 : 1 })`,
                        transformOrigin: 'top left',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        transition: 'none'
                    }}
                >
                    {layers.map((layer: any) => (
                        <LayerRenderer
                            key={`${currentPageIndex}-${layer.id}-${currentPageWidth}-${currentPageHeight}`}
                            layer={layer}
                            layerPositions={currentData.layerPositions}
                            textContents={resolvedBindings.textContents}
                            textStyles={currentData.textStyles}
                            imageSettings={resolvedBindings.imageSettings}
                            shapeSettings={currentData.shapeSettings}
                            backgroundSettings={currentData.backgroundSettings}
                            scrollerSettings={resolvedBindings.scrollerSettings}
                            widgetSettings={currentData.widgetSettings}
                            videoSettings={currentData.videoSettings}
                            currentTime={pageCurrentTime}
                            isPlaying={isPlaying}
                            readOnly={true}
                            canvasScale={1}
                        />
                    ))}
                </div>
            </div>

            {/* Navigation Arrows */}
            {hasMultiplePages && (
                <>
                    <button
                        onClick={() => goToPage((currentPageIndex - 1 + pages.length) % pages.length)}
                        className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full backdrop-blur-md opacity-0 group-hover/preview:opacity-100 transition-all z-50 border border-white/10"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button
                        onClick={() => goToPage((currentPageIndex + 1) % pages.length)}
                        className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full backdrop-blur-md opacity-0 group-hover/preview:opacity-100 transition-all z-50 border border-white/10"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                </>
            )}

            {/* Pagination Dots */}
            {hasMultiplePages && (
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/30 backdrop-blur-xl px-4 py-2 rounded-full border border-white/5 z-50">
                    {pages.map((_: any, idx: number) => (
                        <button
                            key={idx}
                            onClick={() => goToPage(idx)}
                            className={`transition-all duration-300 ${
                                idx === currentPageIndex 
                                    ? 'w-6 h-1.5 bg-blue-500 rounded-full' 
                                    : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70 rounded-full'
                            }`}
                            title={`Page ${idx + 1}`}
                        />
                    ))}
                </div>
            )}
            
            {/* Auto-Play Progress Bar (optional but nice) */}
            {hasMultiplePages && isPlaying && (
                <div className="absolute bottom-0 left-0 h-1 bg-blue-500/50 transition-all duration-100 linear" style={{ width: `${(pageCurrentTime / pageDuration) * 100}%` }} />
            )}
        </div>
    );
};
